interface LegendStyle {
  type: "icon" | "line" | "polygon" | "raster";
  color?: string;
  fillColor?: string;
  fillOpacity?: number;
  weight?: number;
  dashArray?: string;
  icon?: string;
}

interface LegendItemData {
  label: string;
  count?: number;
  countUnavailable?: boolean;
  areaPct?: number;
  range?: { min: number; max: number; mean: number; unit: string };
  areaKm2?: number;
  style: LegendStyle;
}

interface Legend {
  layerName: string;
  category: string;
  items: LegendItemData[];
}

interface ReportLegendsProps {
  legends: Legend[];
  isAreaReport?: boolean;
}

interface SubGroup {
  header: string | null;
  layerTitles: string[];
}

type CategoryGroup = Array<{ layerName: string; items: LegendItemData[] }>;
type RenderedGroup = Array<{ header: string | null; items: LegendItemData[] }>;

const CATEGORY_SUBGROUPS: Record<string, SubGroup[]> = {
  "Divisão política-administrativa": [
    { header: null, layerTitles: ["Municípios"] },
    { header: "Regiões Imediatas", layerTitles: ["Regiões Imediatas"] },
    { header: null, layerTitles: ["Estado", "Estados da fronteira"] },
  ],
  "Infraestrutura de Energia - Planejado": [
    {
      header: "Linhas de Transmissão Planejadas",
      layerTitles: ["Linhas de Transmissão Planejadas"],
    },
    {
      header: "Geração Planejada",
      layerTitles: [
        "Subestações Planejadas",
        "Parques Eólicos Planejados",
        "Usinas Fotovoltáicas Planejadas",
      ],
    },
  ],
  "Infraestrutura de Energia - Em operação": [
    {
      header: "Linhas de Transmissão (SIN - Rede Básica)",
      layerTitles: ["Linhas de Transmissão em Operação"],
    },
    {
      header: "Linhas de Distribuição (Neoenergia)",
      layerTitles: ["69 kV", "138 kV"],
    },
    {
      header: "Subestações",
      layerTitles: [
        "Subestações em Operação (SIN - Rede Básica)",
        "Subestações da rede de distribuição",
      ],
    },
    {
      header: "Geração Renovável",
      layerTitles: [
        "Parques Eólicos em Operação",
        "Usina Fotovoltáicas em Operação",
      ],
    },
    {
      header: "Usinas Hidrelétricas",
      layerTitles: [
        "Usinas Hidrelétricas em Operação",
        "Pequenas Centrais Hidrelétricas",
        "Centrais Geradoras Hidrelétricas",
      ],
    },
    {
      header: "Biomassa e Termelétricas",
      layerTitles: [
        "Usinas de Biomassa",
        "Usina de Combustível Fóssil (Termelétricas)",
      ],
    },
    {
      header: "Gasodutos",
      layerTitles: ["Gasodutos de Transporte", "Gasodutos de Distribuição"],
    },
  ],
  "Fatores Socioambientais": [
    {
      header: "Proteção Territorial",
      layerTitles: [
        "Unidades de Conservação Ambiental",
        "Territórios quilombolas",
        "Terras indígenas (Homologadas)",
        "Unidades Agrícolas Instaladas pelo INCRA",
      ],
    },
    {
      header: "Patrimônio Cultural e Natural",
      layerTitles: [
        "Sítios Arqueológicos",
        "Cavernas",
        "Aves Migratórias (Áreas de concentração)",
      ],
    },
    {
      header: "Áreas Prioritárias para Conservação (MMA)",
      layerTitles: [
        "Áreas prioritárias para conservação da Caatinga (MMA)",
        "Áreas prioritárias para conservação da Mata Atlântica (MMA)",
      ],
    },
    {
      header: "Vulnerabilidade Ambiental",
      layerTitles: ["Áreas suscetíveis à desertificação"],
    },
    {
      header: "Uso do Solo",
      layerTitles: ["Zoneamento Agroecológico de Pernambuco"],
    },
  ],
  "Infraestrutura Logística": [
    {
      header: "Rodovias",
      layerTitles: ["Rodovias Federais", "Rodovias Estaduais"],
    },
    {
      header: "Outros Modais Terrestres",
      layerTitles: ["Transnordestina - Trecho Ferroviário"],
    },
    {
      header: "Metrô do Grande Recife",
      layerTitles: [
        "Linhas de Metrô do Grande Recife",
        "Estações de Metrô do Grande Recife",
      ],
    },
    {
      header: "Terminais e Fluxo",
      layerTitles: ["Portos", "Aeroportos", "Hidrovias (Fluxo de Cabotagem)"],
    },
  ],
  "Mapa Solar e Eólico": [
    {
      header: "Recurso Solar",
      layerTitles: [
        "Potencial Solar (PVOUT)",
        "Irradiação Global Horizontal (GHI)",
      ],
    },
    {
      header: "Recurso Eólico",
      layerTitles: [
        "Densidade de Potência Eólica (100m)",
        "Velocidade do Vento — Pernambuco",
        "Velocidade do Vento — Fernando de Noronha",
      ],
    },
  ],
};

function applySubGroups(
  category: string,
  categoryGroup: CategoryGroup
): RenderedGroup {
  const subGroupConfig = CATEGORY_SUBGROUPS[category];
  if (!subGroupConfig) {
    return [{ header: null, items: categoryGroup.flatMap((g) => g.items) }];
  }

  const consumed = new Set<string>();
  const result: RenderedGroup = [];

  for (const sg of subGroupConfig) {
    const items: LegendItemData[] = [];
    for (const layerTitle of sg.layerTitles) {
      const group = categoryGroup.find((g) => g.layerName === layerTitle);
      if (group) {
        items.push(...group.items);
        consumed.add(layerTitle);
      }
    }
    if (items.length > 0) {
      result.push({ header: sg.header, items });
    }
  }

  const unconsumed = categoryGroup
    .filter((g) => !consumed.has(g.layerName))
    .flatMap((g) => g.items);
  if (unconsumed.length > 0) {
    result.push({ header: null, items: unconsumed });
  }

  return result;
}

function formatLabel(label: string, category: string): string {
  if (category !== "Divisão política-administrativa") return label;
  return label.replace(/ Pernambucano| Pernambucana/g, "").trim();
}

function renderItemValue(
  item: LegendItemData,
  isAreaReport: boolean
): JSX.Element | null {
  if (item.areaPct != null) {
    return (
      <span className="text-[12px] font-bold text-[#0C2856] text-right tabular-nums whitespace-nowrap">
        {item.areaPct.toFixed(1)}%
        {item.areaKm2 != null && (
          <span className="font-normal text-[#6B7280]">
            {" "}
            • {item.areaKm2.toLocaleString("pt-BR")} km²
          </span>
        )}
      </span>
    );
  }
  if (item.range && !isAreaReport) {
    return (
      <span className="text-[12px] font-bold text-[#0C2856] min-w-[80px] text-right tabular-nums">
        {item.range.min.toFixed(1)} – {item.range.max.toFixed(1)}{" "}
        <span className="font-normal text-[11px]">{item.range.unit}</span>
      </span>
    );
  }
  if (item.areaKm2 != null && !isAreaReport) {
    return (
      <span className="text-[12px] font-bold text-[#0C2856] min-w-[80px] text-right tabular-nums">
        {item.areaKm2.toLocaleString("pt-BR")} km²
      </span>
    );
  }
  if (item.count != null && !isAreaReport) {
    return (
      <span className="text-[12px] font-bold text-[#0C2856] min-w-[48px] text-right tabular-nums">
        {item.count.toLocaleString("pt-BR")}
      </span>
    );
  }
  if (item.countUnavailable && !isAreaReport) {
    return (
      <span className="text-[12px] text-[#999] min-w-[48px] text-right">
        —
      </span>
    );
  }
  if (item.style.type === "raster" && !isAreaReport) {
    return (
      <span className="text-[12px] text-[#999] min-w-[48px] text-right">—</span>
    );
  }
  return null;
}

function renderSymbol(style: LegendStyle): JSX.Element {
  switch (style.type) {
    case "icon":
      return (
        <img
          src={style.icon}
          alt=""
          className="w-[18px] h-[18px] object-contain"
        />
      );
    case "line":
      return (
        <div
          style={{
            width: "28px",
            height: "0",
            borderTopWidth: `${style.weight || 2}px`,
            borderTopStyle: style.dashArray ? "dashed" : "solid",
            borderTopColor: style.color || "#333",
          }}
        />
      );
    case "polygon":
      return (
        <div
          style={{
            width: "16px",
            height: "16px",
            backgroundColor: style.fillColor || "transparent",
            opacity: style.fillOpacity !== undefined ? style.fillOpacity : 0.6,
            border: `2px solid ${style.color || "#333"}`,
            borderRadius: "2px",
          }}
        />
      );
    case "raster":
      return (
        <div
          style={{
            width: "28px",
            height: "10px",
            background:
              "linear-gradient(to right, #440154, #31688e, #35b779, #fde725)",
            borderRadius: "2px",
            border: "1px solid rgba(0,0,0,0.2)",
          }}
        />
      );
    default:
      return <div />;
  }
}

function renderLegendItem(
  item: LegendItemData,
  category: string,
  index: number,
  isAreaReport: boolean
): JSX.Element {
  return (
    <div
      key={`${item.label}-${index}`}
      className="flex items-center gap-2 py-0.5"
    >
      <div className="w-8 shrink-0 flex items-center justify-center">
        {renderSymbol(item.style)}
      </div>
      <span className="flex-1 text-[0.78rem] text-[#374151] leading-tight">
        {formatLabel(item.label, category)}
      </span>
      {renderItemValue(item, isAreaReport)}
    </div>
  );
}

export function ReportLegends({
  legends,
  isAreaReport = false,
}: ReportLegendsProps) {
  if (legends.length === 0) {
    return (
      <section className="report-section report-legends my-4">
        <h2 className="text-[1.2rem] font-semibold mb-2.5 pb-1.5 border-b-2 border-[#333] text-[#0C2856]">
          Legendas
        </h2>
        <p className="italic text-[#999] p-5 text-center bg-[#f9f9f9] rounded">
          Nenhuma camada ativa para exibir legendas.
        </p>
      </section>
    );
  }

  const groupedByCategory = new Map<string, CategoryGroup>();

  for (const legend of legends) {
    const validItems = legend.items.filter((item) => {
      if (item.areaPct != null) return item.areaPct > 0;
      if (item.count != null) return item.count !== 0;
      if (isAreaReport && item.areaKm2 != null) return item.areaKm2 > 0;
      if (
        isAreaReport &&
        (legend.category === "Fatores Socioambientais" ||
          legend.category === "Divisão política-administrativa")
      )
        return false;
      return true;
    });
    if (validItems.length === 0) continue;

    const existing = groupedByCategory.get(legend.category) ?? [];
    const existingEntry = existing.find(
      (e) => e.layerName === legend.layerName
    );
    if (existingEntry) {
      existingEntry.items.push(...validItems);
    } else {
      existing.push({ layerName: legend.layerName, items: validItems });
    }
    groupedByCategory.set(legend.category, existing);
  }

  if (groupedByCategory.size === 0) return null;

  return (
    <section className="report-section report-legends my-4">
      <h2 className="text-[1.2rem] font-semibold mb-2.5 pb-1.5 border-b-2 border-[#333] text-[#0C2856]">
        Legendas
      </h2>

      <div className="flex flex-col gap-4">
        {Array.from(groupedByCategory.entries()).map(
          ([category, categoryGroup]) => {
            const renderedGroups = applySubGroups(category, categoryGroup);

            return (
              <div
                key={category}
                className="legend-category rounded-lg border border-[#d7dce5] overflow-hidden break-inside-avoid"
              >
                <div className="bg-[#F7F9FC] px-4 py-2.5 border-b border-[#d7dce5]">
                  <h3 className="text-[12px] font-bold text-[#0C2856] uppercase tracking-wide">
                    {category}
                  </h3>
                </div>

                <div className="px-4 py-3 flex flex-col gap-0">
                  {renderedGroups.map((group, groupIndex) => {
                    if (group.header) {
                      return (
                        <div key={`${group.header}-${groupIndex}`}>
                          <div className="flex items-center gap-2 mt-2 mb-1 -mx-4 px-3 py-1 border-l-[3px] border-l-[#0C2856] bg-[#eef2f7]">
                            <span className="text-[9px] font-bold text-[#475569] uppercase tracking-wider">
                              {group.header}
                            </span>
                          </div>
                          <div className="flex flex-col gap-1.5 pl-2">
                            {group.items.map((item, i) =>
                              renderLegendItem(item, category, i, isAreaReport)
                            )}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={`ungrouped-${groupIndex}`}
                        className="flex flex-col gap-1.5 mt-1.5"
                      >
                        {group.items.map((item, i) =>
                          renderLegendItem(item, category, i, isAreaReport)
                        )}
                      </div>
                    );
                  })}
                  {isAreaReport && category === "Fatores Socioambientais" && (
                    <p className="mt-2 text-[10px] text-[#6B7280] italic border-t border-[#e5e7eb] pt-2">
                      Os percentuais são independentes por camada e representam
                      a proporção de cada fator na área selecionada.
                    </p>
                  )}
                </div>
              </div>
            );
          }
        )}
      </div>
    </section>
  );
}
